/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
class FilterService {
    /** @ngInject */
    constructor($q, $log, configuration) {
        this.$q = $q;
        this.$log = $log;
        this.configuration = configuration;
        this.$log.debug('Service \'FilterService\' loaded');
    }

    /**
     * Builds paging and sorting parameters so as they can be sent in an HTTP request and correctly parsed by the
     * server.
     *
     * @param {Object} pagingAndSorting Paging criteria.
     * @return {Object} The paging parameters for the request.
     */
    buildPagingParams(pagingAndSorting) {
        const pagingAndSortingParams = angular.copy(pagingAndSorting);
        pagingAndSortingParams.page--;
        pagingAndSortingParams.sort = this.buildSortingParam({sort: pagingAndSortingParams.sort}).sort;
        return pagingAndSortingParams;
    }

    /**
     * Builds sorting parameter so as it can be sent in an HTTP request and correctly parsed by the server.
     *
     * @param {Object} sorting Sorting criteria.
     * @return {Object} The sorting parameter for the request.
     */
    buildSortingParam(sorting) {
        const sortingParams = angular.copy(sorting);
        if (sortingParams.sort.startsWith('-')) {
            sortingParams.sort = sortingParams.sort.substring(1) + ',desc';
        }
        return sortingParams;
    }

    /**
     * Builds filters so as they can be sent in an HTTP request and correctly parsed by the server.
     *
     * @param {Object} filters Filters.
     * @return {Object} The filters for the request.
     */
    buildFilters(filters) {
        let filterData = angular.copy(filters);
        // Filters must have a valid value, or be consistent with the operator.
        // - The value may not be an array and be defined.
        // - The value may a non-empty array.
        filterData = filterData.filter(filter => {
            return (!angular.isArray(filter.value) && (filter.value !== undefined) && (filter.value !== null)
                && (filter.value !== '')) || (angular.isArray(filter.value) && (filter.value.length > 0));
        });
        filterData.forEach(filter => {
            if (filter.operator === 'N') {
                filter.negate = (filter.value === true);
                delete filter.value;
            }
            delete filter.placeholderKey;
            delete filter.options;
            // Apply filter custom transformation if set.
            if (filter.transform) {
                filter.transform(filter);
            }
        });
        return filterData;
    }

    /**
     * Maps an entity to a filter option, assuming the entity has an 'id' property, and a 'name' property.
     *
     * @param {Object} entity The entity.
     * @return {Object} Filter option.
     */
    mapOptionWithDefault(entity) {
        return {
            id: entity.id,
            label: entity.name
        };
    }

    /**
     * Gets the options of a filter.
     *
     * @param {RestService} service A REST service.
     * @param {Filter} filter A filter whose UI type is 'SELECT'.
     * @param {String} [sortCriteria] Sort criteria.
     * @param {Object} [filterData] Filter data.
     * @param {Function} [optionMapperFn] Mapper used to map each entity to an option.
     * @return {Promise} A promise providing the options.
     */
    getFilterOptions(service, filter, sortCriteria, filterData, optionMapperFn) {
        const queryData = {};
        if (sortCriteria) {
            queryData.params = this.buildSortingParam({sort: sortCriteria});
        }
        if (filterData) {
            queryData.data = this.buildFilters(filterData);
        }
        filter.options.splice(0);
        const optionMapperFnUsed = optionMapperFn || this.mapOptionWithDefault;
        return service.findEntities(queryData).then(entities => {
            entities.forEach(entity => filter.options.push(optionMapperFnUsed(entity)));
            if (filter.options.every(option => option.id !== filter.value)) {
                delete filter.value;
            }
        });
    }

    /**
     * Gets the options of a filter.
     *
     * @param {RestService} service A REST service.
     * @param {Filter} filter A filter whose UI type is 'SELECT'.
     * @param {String} [sortCriteria] Sort criteria.
     * @param {Object} [filterData] Filter data.
     * @param {Function} [optionMapperFn] Mapper used to map each entity to an option.
     * @param {Object} [criteria] A criteria that will either trigger options retrieval, or reset the filter if missing.
     * @return {Promise} A promise providing the options.
     */
    getOrClearFilterOptions(service, filter, sortCriteria, filterData, criteria, optionMapperFn) {
        filter.options.splice(0);
        let promise;
        if (criteria) {
            promise = this.getFilterOptions(service, filter, sortCriteria, filterData, optionMapperFn);
        } else {
            delete filter.value;
            promise = this.$q.resolve([]);
        }
        return promise;
    }
}

export { FilterService };



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/util/service/FilterService.js